import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import Sider from "common/Home/Sider/Sider.tsx";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import { HeaderSearch } from "types";
import useMedia from "utils/hooks/useMedia.ts";
import {
  HIDE_INTERCOM_LAUNCHER_BREAKPOINT,
  MAIN_CONTENT_ID,
  MOBILE_FILTERS,
  MOBILE_SEARCH,
} from "common/constants.ts";
import styled, { css, useTheme } from "styled-components";
import Banners from "common/Home/Banners/Banners.tsx";
import { useBottomNavigator, useIsOnboarding } from "utils/hooks/misc.tsx";
import useMeasure from "utils/hooks/useMeasure.ts";
import { AuthUser, HeaderFilters } from "@shared/types";
import BottomNavigator from "common/Home/MobileHome/BottomNavigator.tsx";
import SwipeFilters from "common/Filters/FiltersColumn/SwipeFilters.tsx";
import { BOTTOM_SPACER_HEIGHT } from "common/Home/General/constants.tsx";
import { PageWrapperFiltersContext } from "common/Home/PageWrapper/PageWrapperFilters.context.ts";
import Header from "common/Home/Header/Header.tsx";
import PageIntro from "common/NewLook/PageIntro.tsx";
import { NEW_LOOK_CONTENT_MAX } from "common/NewLook/tokens.ts";
import { NEW_LOOK_PAGE_GUTTER } from "common/NewLook/layout.ts";
import {
  OwnedPageIntroConfig,
  PageIntroContext,
} from "common/NewLook/PageIntro.context.ts";
import SwipeBackButton from "common/Home/PageWrapper/SwipeBackButton.tsx";
import { usePageSizeRef } from "common/Home/PageWrapper/PageSizeProvider.tsx";

interface PageWrapperProps {
  user: AuthUser;
}

const PageWrapper: FC<PropsWithChildren<PageWrapperProps>> = ({
  children,
  user,
}) => {
  const [measureBannersRef, bannersMeasurements] = useMeasure<HTMLDivElement>();
  const [filters, setFilters] = useState<HeaderFilters>();
  const [search, setSearch] = useState<HeaderSearch>({
    show: false,
    value: "",
  });
  const [hideMobileFilters, setHideMobileFilters] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileOpen] = useState(false);
  const [titleExtraComponent, setTitleExtraComponent] = useState<ReactNode>();
  const [shouldHideBottomSpacer, setShouldHideBottomSpacer] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pageIntro, setPageIntro] = useState<OwnedPageIntroConfig>({
    config: {},
  });
  const pageIntroContextValue = useMemo(
    () => ({ config: pageIntro.config, setConfig: setPageIntro }),
    [pageIntro]
  );
  const { measureRef } = usePageSizeRef();

  const useMobileFilters = useMedia(
    [`(max-width: ${MOBILE_FILTERS})`],
    [true],
    false
  );
  const useMobileSearch = useMedia(
    [`(max-width: ${MOBILE_SEARCH})`],
    [true],
    false
  );
  const intercomLauncherVisible = useMedia(
    [`(max-width: ${HIDE_INTERCOM_LAUNCHER_BREAKPOINT})`],
    [false],
    true
  );
  const bottomNavigatorData = useBottomNavigator();
  const isBottomNavigator = bottomNavigatorData.baseShouldDisplay;
  const isOnboarding = useIsOnboarding();
  const { isNewLook } = useTheme();
  const isCanvas = isNewLook && !isBottomNavigator && !isOnboarding;
  const hideBottomSpacer = !intercomLauncherVisible || shouldHideBottomSpacer;

  const setShowSearch = (show: boolean) => {
    setSearch((prev) => {
      if (prev.show !== show) {
        return { ...prev, show };
      }
      return prev;
    });
  };

  const setSearchValue = (value: string) => {
    setSearch((prev) => {
      if (prev.value !== value) {
        return { ...prev, value };
      }
      return prev;
    });
  };

  const contextValues = useMemo(() => {
    return {
      setShowSearch,
      setSearchValue,
      setHideMobileFilters,
      mobileFiltersOpen,
      setMobileFiltersOpen,
      search,
      useMobileFilters,
      useMobileSearch,
      distanceTop: bannersMeasurements.height,
      titleExtraComponent,
      setTitleExtraComponent,
      setShouldHideBottomSpacer,
      searchOpen,
      setSearchOpen,
    };
  }, [
    mobileFiltersOpen,
    search,
    useMobileFilters,
    useMobileSearch,
    bannersMeasurements.height,
    titleExtraComponent,
    searchOpen,
  ]);

  const filtersContextValue = useMemo(() => {
    return {
      setFilters,
      filters,
    };
  }, [filters]);

  const banners = useMemo(
    () => (
      <div ref={measureBannersRef}>
        <Banners />
      </div>
    ),
    []
  );

  const toggleMobileMenu = useCallback(
    () => setIsMobileOpen(!isMobileMenuOpen),
    [isMobileMenuOpen]
  );

  return (
    <PageIntroContext.Provider value={pageIntroContextValue}>
      <PageWrapperFiltersContext.Provider value={filtersContextValue}>
        <PageWrapperContext.Provider value={contextValues}>
          <SwipeBackButton>
            <SwipeFilters>
              <StyledLayout $isCanvas={isCanvas}>
                {!isBottomNavigator && !isCanvas && banners}
                <ContentAndHeaderWrapper>
                  <Sider
                    mobileMenuOpen={isMobileMenuOpen}
                    setMobileMenuOpen={setIsMobileOpen}
                  />
                  <InnerContentAndHeaderWrapper $isCanvas={isCanvas}>
                    {isCanvas && banners}
                    <Header
                      toggleMobileMenu={toggleMobileMenu}
                      user={user}
                      hideMobileFilters={hideMobileFilters}
                      banners={isBottomNavigator ? banners : undefined}
                    />
                    <ContentWrapper
                      id={MAIN_CONTENT_ID}
                      ref={measureRef}
                      $hideSpacer={hideBottomSpacer}
                      $isCanvas={isCanvas}
                    >
                      {isCanvas && <PageIntro />}
                      {children}
                    </ContentWrapper>
                  </InnerContentAndHeaderWrapper>
                </ContentAndHeaderWrapper>
                {isBottomNavigator && !user?.isRestrictedWhopUser() && (
                  <BottomNavigator setMenuOpen={setIsMobileOpen} />
                )}
              </StyledLayout>
            </SwipeFilters>
          </SwipeBackButton>
        </PageWrapperContext.Provider>
      </PageWrapperFiltersContext.Provider>
    </PageIntroContext.Provider>
  );
};

export default React.memo(PageWrapper);


const StyledLayout = styled.div<{ $isCanvas: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
  overflow: hidden;
  background: ${(p) => p.theme.colors.layoutBgColor};
  ${(p) =>
    p.$isCanvas &&
    css`
      background: ${p.theme.gradients.pageHaze}, ${p.theme.colors.frameBgColor};
    `}
`;
const ContentWrapper = styled.div<{
  $hideSpacer?: boolean;
  $isCanvas?: boolean;
}>`
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  flex: 1;
  ${(p) =>
    // New look: pages sit on a tinted canvas; their panels are white.
    p.$isCanvas &&
    css`
      background: ${p.theme.colors.canvasBgColor};
      /* Content stays centred at a readable width on wide screens. */
      > * {
        max-width: ${NEW_LOOK_CONTENT_MAX + 2 * NEW_LOOK_PAGE_GUTTER}px;
        margin-left: auto;
        margin-right: auto;
      }
    `}
  ${(p) =>
    !p.$hideSpacer &&
    `
    &:after {
      content: "";
      display: block;
      position: relative;
      min-height: ${BOTTOM_SPACER_HEIGHT}px;
    }
  `}
`;
const ContentAndHeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  flex: 1;
  min-height: 0;
  height: 100%;
`;
const InnerContentAndHeaderWrapper = styled.div<{ $isCanvas: boolean }>`
  width: 100%;
  height: 100%;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.$isCanvas &&
    css`
      /* Flush with the window: no floating frame (gap, radius, shadow)
         around the page, just a hairline separating it from the sider. */
      height: 100%;
      margin: 0;
      overflow: hidden;
      background: ${p.theme.colors.layoutBgColor};
      border-left: 1px solid ${p.theme.colors.cardBorderColor};
    `}
`;
